import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import Button from '../common/Button';

/**
 * Full-screen modal for manually completing pending betslips.
 * Horizontal swipe through legs with fixed header/footer.
 */
const CompleteBetSlipModal = ({
  betslip,
  isOpen,
  onClose,
  onSave,
  onComplete,
}) => {
  const [legResults, setLegResults] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const carouselRef = useRef(null);
  const isScrollingRef = useRef(false);

  const legs = useMemo(() => betslip?.legs || [], [betslip?.legs]);

  // Initialize results when modal opens
  useEffect(() => {
    if (isOpen && legs.length > 0) {
      const initial = {};
      legs.forEach((leg) => {
        initial[leg.id] = leg.result || 'pending';
      });
      setLegResults(initial);
      setCurrentIndex(0);
      setError(null);
    }
  }, [isOpen, legs]);

  // Scroll to current leg when index changes (from arrow clicks)
  useEffect(() => {
    if (carouselRef.current && legs.length > 0) {
      const scrollAmount = currentIndex * carouselRef.current.offsetWidth;
      // Set flag to prevent handleScroll from fighting with programmatic scroll
      isScrollingRef.current = true;
      carouselRef.current.scrollTo({ left: scrollAmount, behavior: 'smooth' });
      // Clear flag after scroll animation completes
      setTimeout(() => {
        isScrollingRef.current = false;
      }, 350); // Slightly longer than typical smooth scroll duration
    }
  }, [currentIndex, legs.length]);

  // Handle scroll snap to update current index (for swipe gestures)
  const handleScroll = () => {
    // Ignore scroll events during programmatic scrolling (arrow clicks)
    if (isScrollingRef.current) return;

    if (carouselRef.current) {
      const scrollLeft = carouselRef.current.scrollLeft;
      const width = carouselRef.current.offsetWidth;
      const newIndex = Math.round(scrollLeft / width);
      if (newIndex !== currentIndex && newIndex >= 0 && newIndex < legs.length) {
        setCurrentIndex(newIndex);
      }
    }
  };

  const setLegResult = (legId, result) => {
    setLegResults((prev) => ({ ...prev, [legId]: result }));
  };

  const goToPrev = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const goToNext = () => {
    if (currentIndex < legs.length - 1) setCurrentIndex(currentIndex + 1);
  };

  const allLegsMarked = Object.values(legResults).every((r) => r !== 'pending');
  const markedCount = Object.values(legResults).filter((r) => r !== 'pending').length;
  const pendingCount = legs.length - markedCount;

  // Get the current leg's selected result (if any)
  const currentLeg = legs[currentIndex];
  const currentLegResult = currentLeg ? legResults[currentLeg.id] : 'pending';
  const hasCurrentSelection = currentLegResult && currentLegResult !== 'pending';

  // Mark all remaining pending legs with the same result as current leg
  const markAllRemaining = () => {
    if (!hasCurrentSelection) return;

    const newResults = { ...legResults };
    legs.forEach((leg) => {
      // Skip locked legs (auto-resolved)
      const isLocked = leg.matched_automatically && leg.result && leg.result !== 'pending';
      if (!isLocked && newResults[leg.id] === 'pending') {
        newResults[leg.id] = currentLegResult;
      }
    });
    setLegResults(newResults);
  };

  const handleSave = async () => {
    if (!onSave) {
      setError('Save function not provided');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const legUpdates = Object.entries(legResults).map(([legId, result]) => ({
        leg_id: parseInt(legId),
        result,
      }));
      const updatedBetslip = await onSave(betslip.id, legUpdates);
      onComplete?.(updatedBetslip);
      onClose();
    } catch (err) {
      console.error('Error saving leg results:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !betslip) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] bg-bg-primary flex flex-col">
      {/* Fixed Header */}
      <div className="flex-shrink-0 bg-bg-card border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <button
            onClick={onClose}
            className="p-2 -ml-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-bg-primary transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="text-center">
            <h2 className="text-lg font-bold text-text-primary">Complete Bet Slip</h2>
            <p className="text-xs text-text-secondary">
              {markedCount} of {legs.length} legs marked
            </p>
          </div>
          <div className="w-10" /> {/* Spacer for centering */}
        </div>

        {/* Progress Dots */}
        <div className="flex justify-center gap-1.5 mt-3">
          {legs.map((leg, idx) => {
            const result = legResults[leg.id];
            let dotClass = 'bg-gray-600'; // pending
            if (result === 'won') dotClass = 'bg-green-500';
            else if (result === 'lost') dotClass = 'bg-red-500';
            else if (result === 'push') dotClass = 'bg-yellow-500';

            return (
              <button
                key={leg.id}
                onClick={() => setCurrentIndex(idx)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${dotClass} ${
                  idx === currentIndex ? 'ring-2 ring-white ring-offset-2 ring-offset-bg-card scale-125' : ''
                }`}
              />
            );
          })}
        </div>
      </div>

      {/* Swipeable Leg Cards */}
      <div
        ref={carouselRef}
        onScroll={handleScroll}
        className="flex-1 flex overflow-x-auto snap-x snap-mandatory scrollbar-hide"
        style={{ scrollSnapType: 'x mandatory' }}
      >
        {legs.map((leg, idx) => {
          const result = legResults[leg.id] || 'pending';
          const isLocked = leg.matched_automatically && leg.result && leg.result !== 'pending';

          return (
            <div
              key={leg.id}
              className="flex-shrink-0 w-full snap-center flex flex-col items-center justify-center p-6"
              style={{ scrollSnapAlign: 'center' }}
            >
              {/* Leg Card */}
              <div className="w-full max-w-sm bg-bg-card rounded-2xl border border-border shadow-xl overflow-hidden">
                {/* Leg Header */}
                <div className="bg-bg-primary p-4 border-b border-border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-accent-blue">
                      LEG {idx + 1} OF {legs.length}
                    </span>
                    {leg.sport && (
                      <span className="text-xs text-text-tertiary uppercase">
                        {leg.sport}
                      </span>
                    )}
                  </div>
                  {isLocked && (
                    <span className="inline-block text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded mb-2">
                      Auto-Resolved
                    </span>
                  )}
                  <h3 className="text-xl font-bold text-text-primary leading-tight">
                    {leg.player_name || `${leg.away_team_name || 'Away'} @ ${leg.home_team_name || 'Home'}`}
                  </h3>
                </div>

                {/* Bet Details */}
                <div className="p-4 border-b border-border">
                  <div className="text-center">
                    <div className="text-2xl font-black text-accent-blue mb-1">
                      {leg.selection}
                      {leg.line_value != null && (
                        <span className="ml-1">
                          {leg.line_value > 0 ? '+' : ''}{leg.line_value}
                        </span>
                      )}
                    </div>
                    {leg.bet_type && (
                      <div className="text-sm text-text-secondary capitalize">
                        {leg.bet_type}
                        {leg.bet_category && ` - ${leg.bet_category}`}
                      </div>
                    )}
                    {leg.odds && (
                      <div className="text-sm text-text-tertiary mt-1">
                        Odds: {leg.odds > 0 ? '+' : ''}{leg.odds}
                      </div>
                    )}
                  </div>
                </div>

                {/* Result Buttons */}
                <div className="p-4">
                  <p className="text-xs text-text-secondary text-center mb-4">
                    {isLocked ? 'Result locked (auto-resolved)' : 'Select result:'}
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    {/* Won */}
                    <button
                      onClick={() => !isLocked && setLegResult(leg.id, 'won')}
                      disabled={isLocked}
                      className={`
                        py-5 rounded-xl font-bold text-lg transition-all
                        flex flex-col items-center justify-center gap-1
                        ${result === 'won'
                          ? 'bg-green-500 text-white ring-4 ring-green-400/50 scale-105'
                          : isLocked
                            ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                            : 'bg-green-600/20 text-green-400 hover:bg-green-600/40 active:scale-95'
                        }
                      `}
                    >
                      <span className="text-2xl">W</span>
                      <span className="text-xs">Won</span>
                    </button>

                    {/* Lost */}
                    <button
                      onClick={() => !isLocked && setLegResult(leg.id, 'lost')}
                      disabled={isLocked}
                      className={`
                        py-5 rounded-xl font-bold text-lg transition-all
                        flex flex-col items-center justify-center gap-1
                        ${result === 'lost'
                          ? 'bg-red-500 text-white ring-4 ring-red-400/50 scale-105'
                          : isLocked
                            ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                            : 'bg-red-600/20 text-red-400 hover:bg-red-600/40 active:scale-95'
                        }
                      `}
                    >
                      <span className="text-2xl">L</span>
                      <span className="text-xs">Lost</span>
                    </button>

                    {/* Push */}
                    <button
                      onClick={() => !isLocked && setLegResult(leg.id, 'push')}
                      disabled={isLocked}
                      className={`
                        py-5 rounded-xl font-bold text-lg transition-all
                        flex flex-col items-center justify-center gap-1
                        ${result === 'push'
                          ? 'bg-yellow-500 text-white ring-4 ring-yellow-400/50 scale-105'
                          : isLocked
                            ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                            : 'bg-yellow-600/20 text-yellow-400 hover:bg-yellow-600/40 active:scale-95'
                        }
                      `}
                    >
                      <span className="text-2xl">P</span>
                      <span className="text-xs">Push</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Swipe Hint */}
              <div className="mt-6 text-center text-text-tertiary text-sm">
                {idx === 0 && legs.length > 1 && (
                  <span>Swipe left for next leg</span>
                )}
                {idx > 0 && idx < legs.length - 1 && (
                  <span>Swipe left or right</span>
                )}
                {idx === legs.length - 1 && legs.length > 1 && (
                  <span>Swipe right for previous</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Navigation Arrows (Desktop) */}
      {legs.length > 1 && (
        <>
          {currentIndex > 0 && (
            <button
              onClick={goToPrev}
              className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center rounded-full bg-bg-card border border-border text-text-primary hover:bg-bg-primary transition-colors"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          {currentIndex < legs.length - 1 && (
            <button
              onClick={goToNext}
              className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center rounded-full bg-bg-card border border-border text-text-primary hover:bg-bg-primary transition-colors"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </>
      )}

      {/* Fixed Footer */}
      <div className="flex-shrink-0 bg-bg-card border-t border-border p-4">
        {error && (
          <div className="mb-3 p-3 bg-red-900/30 border border-red-500/50 rounded-lg text-red-400 text-sm text-center">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-3 max-w-sm mx-auto">
          {/* Batch marking button - shown when current leg has a selection and there are pending legs */}
          {hasCurrentSelection && pendingCount > 0 && (
            <Button
              variant="secondary"
              onClick={markAllRemaining}
              className="w-full"
              disabled={saving}
            >
              Mark {pendingCount} as {currentLegResult.charAt(0).toUpperCase() + currentLegResult.slice(1)}
            </Button>
          )}

          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={onClose}
              className="flex-1"
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={saving || !allLegsMarked}
              className="flex-1"
            >
              {saving ? 'Saving...' : allLegsMarked ? 'Save Results' : `Mark ${pendingCount} More`}
            </Button>
          </div>
        </div>
      </div>

      {/* Hide scrollbar */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default CompleteBetSlipModal;
